import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { TendersService } from '@api/services/tenders.service';
import { ButtonComponent } from '@ui/button/button.component';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { CardComponent } from '@ui/card/card.component';
import { ModalService } from '@ui/modal/modal.service';
import { TendersModalComponent } from './tenders-modal/tenders-modal.component';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { finalize, catchError, EMPTY } from 'rxjs';
import { Tender } from '@api/models/tender.model';
import { IndexResponse } from '@api/models/response.model';

@Component({
  selector: 'app-tenders',
  standalone: true,
  imports: [TableComponent, ButtonComponent, CardComponent],
  template: `
    <div class="h-[calc(100vh-theme(spacing.24))] flex flex-col gap-4">
      <ui-card>
        <div class="flex justify-between items-center">
          <h1 class="text-lg text-slate-900">Aanbestedingen</h1>
          <ui-button [icon]="'add'" (onClick)="addTender()"
            >Nieuwe aanbesteding</ui-button
          >
        </div>
      </ui-card>

      <ui-table
        [columns]="columns"
        [data]="data()"
        [loading]="loading()"
        (onRowClick)="onRowClick($event)"
        [bordered]="false"
        [showPagination]="true"
        [meta]="meta()"
      ></ui-table>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TendersComponent implements OnInit {
  tendersService = inject(TendersService);
  modalService = inject(ModalService);
  confirmService = inject(ConfirmService);
  pageHeader = inject(PageHeaderService);
  private cdr = inject(ChangeDetectorRef);

  columns: TableColumn[] = [
    { key: 'name', label: 'Naam' },
    {
      key: 'end_date',
      label: 'Einddatum',
      format: (value) => new Date(value).toLocaleDateString('nl-NL'),
    },
    { key: 'zoho_category_fieldname', label: 'Zoho Categorie Veld' },
    { key: 'created_at', label: 'Aangemaakt op', format: (value) => new Date(value).toLocaleDateString('nl-NL') },
    {
      key: 'actions',
      label: '',
      width: '200px',
      actions: [
        {
          label: 'Bewerken',
          icon: 'edit',
          variant: 'primary',
          onClick: (row) => this.editTender(row),
        },
        {
          label: 'Verwijderen',
          icon: 'delete',
          variant: 'danger',
          onClick: (row) => this.deleteTender(row),
        },
      ],
    },
  ];

  data = signal<Tender[]>([]);
  meta = signal<IndexResponse<Tender>['meta'] | null>(null);
  loading = signal<boolean>(false);

  ngOnInit() {
    this.getTenders();
    this.pageHeader.setTitle('Aanbestedingen');
  }

  getTenders() {
    this.loading.set(true);
    this.tendersService
      .getTenders()
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        catchError((error) => {
          console.error('Error fetching tenders:', error);
          return EMPTY;
        })
      )
      .subscribe((tenders) => {
        this.data.set(tenders.data);
        this.meta.set(tenders.meta);
      });
  }

  addTender() {
    let modalRef = this.modalService.open(TendersModalComponent);

    modalRef.onClose.subscribe(() => {
      this.getTenders();
    });
  }

  onRowClick(row: any) {
    this.editTender(row);
  }

  editTender(row: any) {
    let modalRef = this.modalService.open(TendersModalComponent, {
      data: {
        tender: row,
      },
    });

    modalRef.onClose.subscribe(() => {
      this.getTenders();
    });
  }

  deleteTender(row: any) {
    let confirm = this.confirmService.confirm({
      title: 'Verwijderen',
      message: 'Weet u het zeker?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
    });

    confirm.then((confirmed) => {
      if (confirmed) {
        this.tendersService.deleteTender(row.id).subscribe(() => {
          this.getTenders();
        });
      }
    });
  }
}
